'use client';

import {createContext, useState, useMemo, useContext, useEffect, ReactNode} from "react";
import {createChat, editChatName, getChatListForCase, removeChat, getChatHistory} from "../../../api/ChatAPI";
import {useParams} from "next/navigation";
import {CaseFile} from "../components/FilesChoice";
import {getFilesForCaseId} from "../../../api/FileAPI";
import {useCases} from "./CasesContext";
import {useTranslation} from "react-i18next";

export interface Chat {
    id: string;
    chatName: string;
    messages: Message[];
    caseId: string;
    created?: string;
}

export interface Message {
    id?: string
    chatId: string;
    response: string;
    query: string;
    attachedFile: any[];
    created: string;
    messageOrder: string;
}

interface CaseChatsContextType {
    caseId: string;
    caseFiles: CaseFile[];
    chatsList: Chat[];
    selectedChat: Chat | null | undefined;
    selectedChatFiles: CaseFile[];
    selectedChatHistory: Message[];
    isChatMessageResponsePending: boolean;
    handleGetChatHistory: (chat: Chat) => void;
    handleSetSelectedChatFiles: (files: CaseFile[]) => void;
    handleSelectChat: (chat: Chat | null) => void;
    handleGetChatsList: (currentChat: Chat | null) => void;
    handleAddNewChat: (count?: number) => void;
    handleCreateNewChat: (chatName: string) => Promise<Chat>;
    handleChangeChatName: (chatId: string, chatName: string) => void;
    handleDeleteChat: (chatId: string) => void;
    handleUpdateIsChatMessageResponsePending: (isPending: boolean) => void;
    setSelectedChatHistory: React.Dispatch<React.SetStateAction<Message[]>>;
}

const CaseChatsContext = createContext<CaseChatsContextType>({} as CaseChatsContextType);


export const CaseChatsProvider = ({children}: {children: ReactNode;}) => {
    const {selectedCase} = useCases();
    const caseId = selectedCase ? selectedCase.id : "";

    const [caseFiles, setCaseFiles] = useState<CaseFile[]>([]);
    const [chatsList, setChatsList] = useState<Chat[]>([]);
    const [selectedChat, setSelectedChat] = useState<Chat | undefined>();
    const [selectedChatFiles, setSelectedChatFiles] = useState<CaseFile[]>([]);
    const [selectedChatHistory, setSelectedChatHistory] = useState<Message[]>([]);
    const [isChatMessageResponsePending, setIsChatMessageResponsePending] = useState<boolean>(false);

    const handleGetChatHistory = (chat: Chat) => {
        getChatHistory(caseId, chat.id)
            .then((res: Message[]) => {
                setSelectedChatHistory(res);
                if (res.length > 0) {
                    setSelectedChatFiles(res[0].attachedFile);
                }
            })
            .catch(() => {});
    };

    const handleSetSelectedChatFiles = (files: CaseFile[]) => {
        setSelectedChatFiles(files);
    };

    const handleSelectChat = (chat: Chat | null) => {
        setIsChatMessageResponsePending(false);
        if (!chat && chatsList.length > 0) {
            setSelectedChat(chatsList[0]);
            handleGetChatHistory(chatsList[0]);
        } else if (!chat) {
            setSelectedChat(undefined);
            setSelectedChatHistory([]);
            setSelectedChatFiles([]);
        } else {
            setSelectedChat(chat);
            handleGetChatHistory(chat);
        }
    };

    const handleGetChatsList = (currentChat: Chat | null) => {
        if (!caseId) return;

        getChatListForCase(caseId).then((value: Chat[]) => {
            const sortedChats = value.sort((a, b) =>
                new Date(b.created ?? "").getTime() - new Date(a.created ?? "").getTime()
            );
            setChatsList(sortedChats);

            if (!selectedChat || selectedChat.caseId !== caseId || sortedChats.length === 0) {
                handleAddNewChat(sortedChats.length + 1);
            } else if (!selectedChat || !currentChat) {
                handleSelectChat(sortedChats[0]);
            } else if (!currentChat && sortedChats.length === 0) {
                handleSelectChat(null);
            }
        });
    };

    const handleChangeChatName = (chatId: string, chatName: string) => {
        editChatName(caseId, chatId, chatName).then((editedChat: Chat) => {
            handleGetChatsList(editedChat);
            if (selectedChat?.id === editedChat.id) {
                setSelectedChat(editedChat);
            }
        });
    };

    const handleDeleteChat = (chatId: string) => {
        //NASTY, workaround
        removeChat(caseId, chatId).then(() => {
            getChatListForCase(caseId).then((value: Chat[]) => {
                const sortedChats = value.sort((a, b) =>
                    new Date(b.created ?? "").getTime() - new Date(a.created ?? "").getTime()
                );
                setChatsList(sortedChats);
                handleAddNewChat(sortedChats.length + 1);
            });
        });
    };

    const handleCreateNewChat = (chatName: string): Promise<Chat> => {
            return new Promise((resolve, reject) => {
                createChat(chatName, caseId)
                    .then((newChat: Chat) => {
                        handleGetChatsList(newChat);
                        setSelectedChat(newChat);
                        resolve(newChat);
                    })
                    .catch(reject);
            });
    };

    const handleAddNewChat = (count?: number) => {
        const tempChat: Chat = {
            id: "",
            chatName: `Chat #${count ?? chatsList.length + 1}`,
            messages: [],
            caseId,
        };

        setSelectedChatHistory([]);
        setSelectedChatFiles([]);
        setSelectedChat(tempChat);
        setIsChatMessageResponsePending(false);
    };

    const handleUpdateIsChatMessageResponsePending = (isPending: boolean) => {
        setIsChatMessageResponsePending(isPending);
    };

    useEffect(() => {
        if (selectedCase?.id) {
            setIsChatMessageResponsePending(false);
            getFilesForCaseId(selectedCase.id).then((files: CaseFile[]) => {
                setCaseFiles(files);
            });
            handleGetChatsList(null);
        }
    }, [selectedCase]);

    useEffect(() => {
        if (selectedChat?.id && selectedChatHistory.length > 1) {
            handleGetChatHistory(selectedChat);
        }
    }, [selectedChat]);

    const value = useMemo<CaseChatsContextType>(
        () => ({
            caseId,
            caseFiles,
            chatsList,
            selectedChat,
            selectedChatFiles,
            selectedChatHistory,
            isChatMessageResponsePending,
            handleGetChatHistory,
            handleSetSelectedChatFiles,
            handleSelectChat,
            handleGetChatsList,
            handleChangeChatName,
            handleDeleteChat,
            handleCreateNewChat,
            handleAddNewChat,
            handleUpdateIsChatMessageResponsePending,
            setSelectedChatHistory,
        }),
        [
            caseId,
            caseFiles,
            chatsList,
            selectedChat,
            selectedChatFiles,
            selectedChatHistory,
            isChatMessageResponsePending,
        ]
    );

    return (
        <CaseChatsContext.Provider value={value}>
            {children}
        </CaseChatsContext.Provider>
    );
};

export const useCaseChats = (): CaseChatsContextType => useContext(CaseChatsContext);
